<template>
  <component
    :is="itemTag"
    :id="tabId"
    ref="tabElement"
    v-ds-tooltip="tooltip"
    v-bind="itemElementAttrs"
    role="tab"
    :class="itemClasses"
    :aria-selected="props.active"
    :aria-disabled="props.disabled || undefined"
    :aria-label="itemAriaLabel"
    :aria-controls="props.item.panelId"
    :tabindex="props.tabIndex"
    :data-cy="`ds-tabs__item--${convertStringToDataCy(props.item.value)}`"
    @click="selectTab"
  >
    <span
      :class="DS_TABS_CLASSES.ItemContent"
      data-cy="ds-tabs__item-content"
    >
      <DSIcon
        v-if="showIcon"
        :icon="props.item.icon!"
        size="md"
        :class="DS_TABS_CLASSES.Icon"
        data-cy="ds-tabs__icon"
      />

      <span
        v-if="showText"
        :class="DS_TABS_CLASSES.Text"
        data-cy="ds-tabs__text"
      >
        {{ props.item.label }}
      </span>
    </span>

    <DSBadge
      v-if="hasBadge"
      :text="badgeText"
      state="danger"
      size="lg"
      :disabled="props.disabled"
      :class="badgeClasses"
      data-cy="ds-tabs__badge"
    />
  </component>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import DSBadge from "@/src/design_system/data_display/badge/components/DSBadge.vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import { DS_STATE_CLASSES } from "@/src/design_system/shared/constants/stateClasses";
import { DS_DIRECTION } from "@/src/design_system/shared/types/direction";
import { convertStringToDataCy } from "@/src/shared/utils/playwright";
import { DS_TABS_CLASSES } from "../constants/classes";
import { DS_TABS_VARIANT, type DSTabProps } from "../types/props";
import type { DSTabEmits } from "../types/emits";
import { vDsTooltip } from "@/src/design_system/primitives/tooltip/directives/vDsTooltip";

const props = withDefaults(defineProps<DSTabProps>(), {
  active: false,
  disabled: false,
  direction: DS_DIRECTION.Horizontally,
  variant: DS_TABS_VARIANT.Default,
  tabIndex: -1,
});

const emit = defineEmits<DSTabEmits>();

const tabElement = ref<HTMLElement | null>(null);

const itemAriaLabel = computed(() => props.item.ariaLabel ?? props.item.label);
const tabId = computed(
  () => props.item.tabId ?? (props.item.panelId ? `ds-tabs-tab-${convertStringToDataCy(props.item.value)}` : undefined)
);

const tooltip = computed(() => {
  const enabled = props.variant === DS_TABS_VARIANT.OnlyIcons || props.item.showTooltip;
  return enabled ? (props.item.tooltipText ?? props.item.label ?? props.item.ariaLabel) : undefined;
});

const hasBadge = computed(() => props.item.badgeText !== undefined);
const badgeText = computed(() => (hasBadge.value ? String(props.item.badgeText) : undefined));
const showFloatingBadge = computed(
  () => hasBadge.value && (props.direction === DS_DIRECTION.Vertically || props.variant !== DS_TABS_VARIANT.Default)
);
const badgeClasses = computed(() => ({
  [DS_TABS_CLASSES.Badge]: true,
  [DS_TABS_CLASSES.BadgeFloating]: showFloatingBadge.value,
}));

const itemTag = computed(() => {
  if (props.item.href) return "a";

  return "button";
});

const itemElementAttrs = computed(() => {
  if (props.item.href) {
    return {
      href: props.disabled ? undefined : props.item.href,
    };
  }

  return {
    type: "button",
    disabled: props.disabled,
  };
});

const itemClasses = computed(() => ({
  [DS_TABS_CLASSES.Item]: true,
  [DS_TABS_CLASSES.ItemActive]: props.active,
  [DS_TABS_CLASSES.ItemDisabled]: props.disabled,
  [DS_STATE_CLASSES.Selected]: props.active,
  [DS_STATE_CLASSES.Disabled]: props.disabled,
}));

const showIcon = computed(() => props.variant !== DS_TABS_VARIANT.OnlyTexts && Boolean(props.item.icon));

const showText = computed(() => props.variant !== DS_TABS_VARIANT.OnlyIcons && Boolean(props.item.label));

const selectTab = (event: MouseEvent) => {
  if (props.disabled) {
    event.preventDefault();
    return;
  }

  emit("select", props.item);
};

const focus = () => {
  tabElement.value?.focus();
};

const matchesTarget = (target: EventTarget | null): boolean => target === tabElement.value;

defineExpose({
  focus,
  matchesTarget,
});
</script>
